import { HttpHeaders, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Keycloak from 'keycloak-js';
import { KeycloakLoginOptions } from 'keycloak-js/lib/keycloak';
import { from } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from '../../services/logger.service';
import { AuthRedirectDebugService } from '../auth-redirect-debug.service';
import { KeycloakEventHandlers } from '../models/keycloak-event-handlers.model';
import { KeycloakClientInitOptions } from '../models/keycloak-init.model';
import {
    AuthTokenDiagnostics,
    buildAuthTokenDiagnostics,
} from '../utils/auth-token-diagnostics.util';

@Injectable({
    providedIn: 'root',
})
export class KeycloakClientService {
    private instance?: Keycloak;
    private pendingEventHandlers?: KeycloakEventHandlers;
    private initialized = false;
    private enableBearerInterceptor = true;
    private authorizationHeaderName = 'Authorization';
    private bearerPrefix = 'Bearer ';
    private updateMinValidity = 60;

    shouldAddToken: (request: HttpRequest<unknown>) => boolean = () => true;
    shouldUpdateToken: (request: HttpRequest<unknown>) => boolean = () => true;

    constructor(
        private authRedirectDebug: AuthRedirectDebugService,
        private logger: LoggerService
    ) {}

    async init(options: KeycloakClientInitOptions): Promise<boolean> {
        this.enableBearerInterceptor = options.enableBearerInterceptor ?? true;
        this.authorizationHeaderName = options.authorizationHeaderName ?? 'Authorization';
        this.bearerPrefix = (options.bearerPrefix ?? 'Bearer').trim().concat(' ');
        this.updateMinValidity = options.updateMinValidity;
        this.shouldAddToken = options.shouldAddToken ?? (() => true);
        this.shouldUpdateToken = options.shouldUpdateToken ?? (() => true);

        this.instance = new Keycloak(options.config);
        this.authRedirectDebug.setKeycloakInstance(this.instance);

        if (this.pendingEventHandlers) {
            this.applyEventHandlers(this.pendingEventHandlers);
        }

        const authenticated = await this.instance.init(options.initOptions);

        if (authenticated) {
            this.initialized = true;
            this.authRedirectDebug.markLogin();
            // Prevent keycloak-js from auto-calling login() on clearToken() after a failed refresh.
            this.instance.loginRequired = false;
            this.logger.debug(
                '[AuthToken] Session established after init',
                JSON.stringify(this.getRefreshDiagnostics())
            );
        }

        return authenticated;
    }

    bindEventHandlers(handlers: KeycloakEventHandlers): void {
        if (!this.instance) {
            this.pendingEventHandlers = handlers;
            return;
        }

        this.applyEventHandlers(handlers);
    }

    private applyEventHandlers(handlers: KeycloakEventHandlers): void {
        if (!this.instance) {
            return;
        }

        if (handlers.onAuthError) {
            this.instance.onAuthError = handlers.onAuthError;
        }
        if (handlers.onAuthLogout) {
            this.instance.onAuthLogout = handlers.onAuthLogout;
        }
        if (handlers.onAuthRefreshSuccess) {
            this.instance.onAuthRefreshSuccess = handlers.onAuthRefreshSuccess;
        }
        if (handlers.onAuthRefreshError) {
            this.instance.onAuthRefreshError = handlers.onAuthRefreshError;
        }
        if (handlers.onAuthSuccess) {
            this.instance.onAuthSuccess = handlers.onAuthSuccess;
        }
        if (handlers.onTokenExpired) {
            this.instance.onTokenExpired = handlers.onTokenExpired;
        }
        if (handlers.onActionUpdate) {
            this.instance.onActionUpdate = handlers.onActionUpdate;
        }
        if (handlers.onReady) {
            this.instance.onReady = handlers.onReady;
        }
    }

    isInitialized(): boolean {
        return this.initialized;
    }

    isLoggedIn(): boolean {
        return !!this.instance?.authenticated;
    }

    getUpdateMinValidity(): number {
        return this.updateMinValidity;
    }

    isTokenExpired(minValidity = 0): boolean {
        return this.instance?.isTokenExpired(minValidity) ?? false;
    }

    isTokenNearExpiry(): boolean {
        if (!this.instance) {
            return false;
        }

        return this.isTokenExpired(this.updateMinValidity);
    }

    async updateToken(minValidity = this.updateMinValidity): Promise<boolean> {
        if (!this.instance) {
            throw new Error('Keycloak client is not initialized.');
        }

        return this.instance.updateToken(minValidity);
    }

    getToken(): string | undefined {
        return this.instance?.token;
    }

    async login(options: KeycloakLoginOptions = {}): Promise<void> {
        if (!this.instance) {
            throw new Error('Keycloak client is not initialized.');
        }

        await this.instance.login(options);
    }

    async logout(redirectUri?: string): Promise<void> {
        if (!this.instance) {
            throw new Error('Keycloak client is not initialized.');
        }

        await this.instance.logout({ redirectUri });
    }

    addTokenToHeader(headers: HttpHeaders = new HttpHeaders()) {
        const token = this.getToken();

        return from(Promise.resolve(token)).pipe(
            map(value =>
                value
                    ? headers.set(this.authorizationHeaderName, this.bearerPrefix + value)
                    : headers
            )
        );
    }

    getInstance(): Keycloak | undefined {
        return this.instance;
    }

    getRefreshDiagnostics(minValidity?: number): AuthTokenDiagnostics {
        const validity = minValidity ?? this.updateMinValidity;
        return buildAuthTokenDiagnostics(this.instance, validity);
    }

    get bearerInterceptorEnabled(): boolean {
        return this.enableBearerInterceptor;
    }
}
